<template>
  <div>
    <div v-if="$route.name === 'settingsUsers'" class="p-users">
      <div class="p-toolbar">
        <div class="p-toolbar-item p-search">
          <p-search-input
            v-model="search"
            :items="searchSuggestions"
          ></p-search-input>
        </div>
        <div class="p-toolbar-item p-right">
          <v-btn
            :to="{ name: 'settingsUsersAccessSchemes'}"
            color="primary"
            class="p-btn-text"
            text
            large
          >
            {{ $t('user.accessManagement') }}
          </v-btn>
          <v-btn
            v-if="hasAccess()"
            class="p-btn-rounded ml-4 mr-1"
            large
            rounded
            @click="inviteDialog = true"
          >
            {{ $t('user.inviteUser') }}
          </v-btn>
        </div>
      </div>

      <v-row>
        <v-col>
          <v-data-table
            :headers="tableHeaders"
            :items="usersComputed"
            :search="search"
            :page.sync="page"
            :items-per-page="itemsPerPage"
            class="p-no-borders"
            hide-default-footer
            @page-count="pageCount = $event"
          >
            <template v-slot:[`item.fullName`]="{ item }">
              <p-user-item
                :value="item"
                font-bold
                you
              ></p-user-item>
            </template>
            <template v-slot:[`item.changed`]="{ item }">
              <v-tooltip v-if="item.changed" bottom>
                <template v-slot:activator="{ on }">
                  <v-btn icon v-on="on">
                    <span v-if="item.changed" class="item-changed"></span>
                  </v-btn>
                </template>
                {{ $t('user.table.changedTooltip') }}
              </v-tooltip>
            </template>
            <template v-slot:[`item.isActive`]="{ item }">
            <span
              v-if="item.id === currentUser.id"
              :class="currentUserStatus.class"
            >
              {{ currentUserStatus.text }}
            </span>
              <v-select
                v-else
                v-model="item.editedIsActive"
                :items="statusItems"
                :disabled="!hasAccess()"
                class="p-users__select"
                item-color=""
                hide-details
                outlined
                dense
                @change="setUserIsActive_(item)"
              >
                <template v-slot:item="{ item }">
                  <span :class="item.class">{{ item.text }}</span>
                </template>
                <template v-slot:selection="{ item }">
                  <span :class="item.class">{{ item.text }}</span>
                </template>
              </v-select>
            </template>
            <template v-if="hasAccess()" v-slot:[`item.role`]="{ item }">
              <template v-if="item.id === currentUser.id">{{ currentUser.role.name }}</template>
              <v-select v-else
                        v-model="item.editedRoleId"
                        :items="roles"
                        :disabled="!hasAccess()"
                        class="p-users__select"
                        item-value="id"
                        item-text="name"
                        hide-details
                        outlined
                        dense
                        @change="setUserRole_(item)"
              ></v-select>
            </template>
            <template v-if="hasAccess()" v-slot:[`item.actions`]="{ item }">
              <div class="p-table-row-actions">
                <p-btn-icon v-if="item.id === currentUser.id" type="lock"></p-btn-icon>
                <p-btn-icon v-else type="delete" @click="openDeleteDialog(item)"></p-btn-icon>
              </div>
            </template>
          </v-data-table>

          <v-pagination
            v-model="page"
            :length="pageCount"
            :total-visible="15"
            class="mt-5"
          ></v-pagination>
        </v-col>
      </v-row>

      <invite-dialog :dialog.sync="inviteDialog"></invite-dialog>
    </div>
    <div v-else class="p-users-access">
      <router-view/>
    </div>

    <p-delete-dialog
      :dialog.sync="deleteDialog"
      :item="deletedUser"
      :item-data="deletedUserData"
      :load-relations="loadRelations"
      :delete-item="deleteUser"
      @itemDeleted="onItemDeleted"
    ></p-delete-dialog>
  </div>
</template>


<script>
import { mapGetters, mapActions } from 'vuex'

import PUserItem from '@/components/PUserItem'
import PSearchInput from '@/components/PSearchInput'
import PBtnIcon from '@/components/PBtnIcon'
import PDeleteDialog from '@/components/PDeleteDialog'

import InviteDialog from './UserInviteDialog'

import snackbarMixin from '@/mixins/snackbar.mixin'
import pageMixin from '@/mixins/page.mixin'
import accessMixin from '@/mixins/access.mixin'

import eventBus from '@/eventBus'


export default {
  components: {
    PUserItem,
    PSearchInput,
    PBtnIcon,
    PDeleteDialog,
    InviteDialog
  },


  mixins: [
    snackbarMixin,
    pageMixin,
    accessMixin
  ],


  data () {
    return {
      search: '',
      page: 1,
      pageCount: 0,
      itemsPerPage: 20,
      inviteDialog: false,
      deleteDialog: false,
      deletedUser: null,
      deletedUserData: null
    }
  },


  computed: {
    ...mapGetters('role', ['roles']),
    ...mapGetters('user', ['users', 'currentUserId', 'currentUser']),
    ...mapGetters('changes', ['isItemChanged']),


    usersComputed () {
      if (this.hasAccess()) {
        return this.users.map(user => {
          return {
            ...user,
            editedRoleId: user?.role?.id,
            editedIsActive: user?.isActive,
            changed: user?.id ? this.isItemChanged(user.id) : false
          }
        })
      } else {
        return this.users.map(user => {
          return {
            ...user,
            editedIsActive: user?.isActive,
            changed: user?.id ? this.isItemChanged(user.id) : false
          }
        })
      }
    },

    searchSuggestions () {
      const items = []

      for (const user of this.users) {
        items.push(user.fullName)
        items.push(user.email)
      }

      return items
    },

    statusItems () {
      return [
        { value: true, text: this.$t('user.status.active'), class: 'pGreen--text' },
        { value: false, text: this.$t('user.status.inactive'), class: '' }
      ]
    },

    currentUserStatus () {
      return this.statusItems.find(x => x.value === this.currentUser.isActive)
    },

    tableHeaders () {
      if (this.hasAccess()) {
        return [
          { text: this.$t('user.table.name'), value: 'fullName' },
          { text: '', value: 'changed' },
          { text: this.$t('user.table.email'), value: 'email' },
          { text: this.$t('user.table.status'), value: 'isActive', filterable: false },
          { text: this.$t('user.table.role'), value: 'role', filterable: false },
          { text: '', value: 'actions', align: 'end', sortable: false, filterable: false }
        ]
      } else {
        return [
          { text: this.$t('user.table.name'), value: 'fullName' },
          { text: this.$t('user.table.email'), value: 'email' },
          { text: this.$t('user.table.status'), value: 'isActive', filterable: false }
        ]
      }
    }
  },


  methods: {
    ...mapActions('user', ['loadUsers', 'loadUsersShort', 'setUserIsActive', 'setUserRole', 'loadRelations', 'deleteUser']),
    ...mapActions('role', ['loadRoles']),

    async searchUsers (val) {
      this.search = val
    },

    initEventBus () {
      eventBus.$on('goToEntity', async (entityTitle) => {
        this.searchUsers(entityTitle)
      })

      eventBus.$on('loadEntities', async () => {
        await this.loadUsers()
      })
    },

    async setUserIsActive_ (user) {
      try {
        this.showSnackbarProcessing()
        await this.setUserIsActive({ userId: user.id, isActive: user.editedIsActive })
        this.showSnackbarSuccess(this.$t('saved'))
      } catch (error) {
        this.showSnackbarError()
        this.$log.error(error)
        user.editedIsActive = user.isActive
      }
    },

    async setUserRole_ (user) {
      try {
        this.showSnackbarProcessing()
        await this.setUserRole({ userId: user.id, roleId: user.editedRoleId })
        this.showSnackbarSuccess(this.$t('saved'))
      } catch (error) {
        this.showSnackbarError()
        this.$log.error(error)
        user.editedRoleId = user?.role?.id
      }
    },

    async deleteUser_ (user) {
      try {
        const confirm = await this.$confirm(this.$t('user.deleteUserConfirm', { name: user.fullName }), { trueText: this.$t('delete') })
        if (confirm === true) {
          this.showSnackbarProcessing()
          await this.deleteUser(user.id)
          this.showSnackbarSuccess(this.$t('user.userDeleted'))
        }
      } catch (error) {
        this.showSnackbarError()
        this.$log.error(error)
      }
    },

    openDeleteDialog (user) {
      try {
        if (user) {
          this.deletedUser = user
          this.deletedUserData = {
            type: this.$t('entity.user'),
            title: this.deletedUser.fullName
          }
        }
        this.deleteDialog = true
      } catch (error) {
        this.showSnackbarError()
        this.$log.error(error)
      }
    },

    onItemDeleted () {
      this.showSnackbarSuccess(this.$t('user.userDeleted'))
    }
  },


  async created () {
    this.initEventBus()
    try {
      this.startPageLoading()
      if (this.hasAccess()) {
        await Promise.all([
          this.loadRoles(),
          this.loadUsers()
        ])
      } else {
        await Promise.all([
          this.loadUsers()
        ])
      }
      if (this.$router.currentRoute.params?.goto) {
        await this.searchUsers(this.$router.currentRoute.params?.goto)
      }
    } catch (error) {
      this.showSnackbarError()
      this.$log.error(error)
    } finally {
      this.stopPageLoading()
    }
  }
}
</script>
